import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Pane } from "evergreen-ui";
import { QueryInput } from "../../../../common/query-filters";
import { TablePageHeader } from "../../common/tables/view/TablePageHeader";
import {
    AbstractTableViewProvider
} from "../../common/tables/view/AbstractTableViewProvider";
import {
    queryAllCostPayoutModifiers,
    useCreateCostPayoutModifierMutation,
    useUpdateCostPayoutModifierMutation,
} from "../../datastore/CostPayoutModifierDataStore";
import {
    CostPayoutModifierTableRow
} from "../../../../common/tables/cost-payout-modifier/CostPayoutModifierTableRow";
import { CostPayoutModifier } from "../../../../common/models";
import { CostPayoutModifierCreateInput } from "../../../../common/inputs/CostPayoutModifierInput";
import { DefaultCPMTableConfig } from "./tables/config/DefaultCPMTableConfig";
import { CPMTableCellFactory } from "./factory/CPMTableCellFactory";
import { TableCellFactory } from "../../common/tables/factory";
import { TableView } from "../../common/tables/view/TableView";
import { CPMTableRowSideSheetForm } from "./tables/CPMTableRowSideSheetForm";
import { CPMDetailSideSheet } from "./CPMDetailSideSheet";
import { RampartAuthorized } from "../../rampart/RampartAuthorized";
import { normalizeCountryCode, isValidCountryCode, isValidEmailDomain, parseEmailDomainInput } from "../../../../common/utils/inputValidation";

const defaultCostPayoutModifier: Partial<CostPayoutModifierCreateInput> = {
    priority: 0,
};

export const CostPayoutModifierListPage: FC = () => {
    const [showCreateRowForm, setShowCreateRowForm] = useState<boolean>(false);
    const [cpmCreateInput, setCpmCreateInput] =
        useState<Partial<CostPayoutModifierCreateInput>>(defaultCostPayoutModifier);

    // Detail side sheet state
    const [detailSideSheetCpm, setDetailSideSheetCpm] = useState<CostPayoutModifier | null>(null);
    const [detailSideSheetTabIndex, setDetailSideSheetTabIndex] = useState<number>(0);
    const [showDetailSideSheet, setShowDetailSideSheet] = useState<boolean>(false);

    const onOpenDetailSideSheet = useCallback((cpm: CostPayoutModifier, tabIndex: number) => {
        setDetailSideSheetCpm(cpm);
        setDetailSideSheetTabIndex(tabIndex);
        setShowDetailSideSheet(true);
    }, []);

    const onDismissDetailSideSheet = useCallback(() => {
        setShowDetailSideSheet(false);
        setDetailSideSheetCpm(null);
    }, []);

    const cpmTableCellFactory: TableCellFactory<CostPayoutModifierTableRow, CostPayoutModifier> = useMemo(() => {
        return new CPMTableCellFactory({
            onOpenDetailSideSheet,
        });
    }, [onOpenDetailSideSheet]);

    const queryTableData = useCallback((input: QueryInput<CostPayoutModifierTableRow>) => {
        return queryAllCostPayoutModifiers({
            take: 1000,
            sortColumn: input.sortColumn as string,
            sortOrder: input.sortOrder,
        });
    }, []);

    const [
        createCostPayoutModifier,
        {
            loading: createLoading,
            error: createError,
            called: createCalled,
        }
    ] = useCreateCostPayoutModifierMutation();

    const [
        updateCostPayoutModifier,
        {
            loading: updateLoading,
            called: updateCalled,
            error: updateError,
        }
    ] = useUpdateCostPayoutModifierMutation();

    const onOpenSideSheet = useCallback(() => {
        setShowCreateRowForm(true);
    }, []);

    const onSubmitSideSheet = useCallback(() => {
        const cc = normalizeCountryCode(cpmCreateInput.conditionCountryCode);
        const input: CostPayoutModifierCreateInput = {
            name: cpmCreateInput.name!,
            conditionCountryCode: cc || undefined,
            conditionEmailDomains: typeof cpmCreateInput.conditionEmailDomains === "string"
                ? parseEmailDomainInput(cpmCreateInput.conditionEmailDomains)
                : cpmCreateInput.conditionEmailDomains,
            conditionIsBundle: cpmCreateInput.conditionIsBundle,
            conditionSourceName: cpmCreateInput.conditionSourceName || undefined,
            conditionFulfillmentService: cpmCreateInput.conditionFulfillmentService || undefined,
            priority: cpmCreateInput.priority ?? 0,
        };
        createCostPayoutModifier(input);
    }, [createCostPayoutModifier, cpmCreateInput]);

    const onCancelSideSheet = useCallback(() => {
        setShowCreateRowForm(false);
    }, [setShowCreateRowForm]);

    const onUpdateRow = useCallback((
        costPayoutModifiers: CostPayoutModifier[],
        id: string | number,
        columnName: keyof CostPayoutModifierTableRow,
        value: string | number | boolean | null,
        _label?: string,
    ): CostPayoutModifier[] => {
        return costPayoutModifiers.map((cpm: CostPayoutModifier) => {
            if (cpm.id !== id) {
                return cpm;
            }

            let resolvedValue: string | number | boolean | string[] | null = value;
            if (columnName === "conditionCountryCode" && typeof value === "string") {
                const normalized = normalizeCountryCode(value);
                if (normalized !== "" && !isValidCountryCode(normalized)) {
                    return cpm;
                }
                resolvedValue = normalized || null;
            }
            if (columnName === "conditionEmailDomains" && typeof value === "string") {
                const entries = parseEmailDomainInput(value);
                if (entries.length > 0 && entries.some(d => !isValidEmailDomain(d))) {
                    return cpm;
                }
                resolvedValue = entries;
            }
            if (columnName === "conditionIsBundle" && typeof value === "string") {
                resolvedValue = value === "true";
            }

            const cpmToUpdate = {
                ...cpm,
                [columnName]: resolvedValue,
            };

            updateCostPayoutModifier(cpmToUpdate);

            return cpmToUpdate;
        });
    }, [updateCostPayoutModifier]);

    const emptyViewProps = useMemo(() => {
        return {
            primaryText: "No cost payout modifiers found",
            secondaryText: "Add Modifier",
            onClickSecondaryText: () => setShowCreateRowForm(true),
        };
    }, []);

    const errorViewProps = useMemo(() => {
        return {
            primaryText: "Failed to load cost payout modifiers",
            secondaryText: "Refresh the page to try again",
        };
    }, []);

    useEffect(() => {
        if (!createCalled) {
            return;
        }

        if (!createLoading && !createError) {
            setCpmCreateInput(defaultCostPayoutModifier);
        }
    }, [createLoading, createCalled, createError]);

    return (
        <RampartAuthorized requiredGroups={["juniper.self-serve.admin"]}>
            <Pane height={"100%"} display={"flex"} flexDirection={"column"}>
                <TablePageHeader
                    title="Cost Payout Modifiers"
                    addButtonText="Add Modifier"
                    addButtonOnClick={onOpenSideSheet}
                />
                <AbstractTableViewProvider
                    queryTableData={queryTableData}
                    onUpdateRow={onUpdateRow}
                    updateRowLoading={updateLoading}
                    updateRowCalled={updateCalled}
                    updateRowError={updateError}
                    createRowCalled={createCalled}
                    createRowError={createError}
                    createRowLoading={createLoading}
                    tableCellFactory={cpmTableCellFactory}
                    savedTableConfigProps={{
                        tableName: "costPayoutModifiers",
                        identifiers: ["v5"],
                        defaultConfigValue: DefaultCPMTableConfig,
                    }}
                >
                    <TableView
                        errorViewProps={errorViewProps}
                        emptyViewProps={emptyViewProps}
                    />
                </AbstractTableViewProvider>
                <CPMTableRowSideSheetForm
                    isShown={showCreateRowForm}
                    costPayoutModifier={cpmCreateInput}
                    mutationLoading={createLoading}
                    mutationCalled={createCalled}
                    mutationError={createError}
                    onDismiss={onCancelSideSheet}
                    onSubmit={onSubmitSideSheet}
                    onInputChanged={setCpmCreateInput}
                />
                <CPMDetailSideSheet
                    isShown={showDetailSideSheet}
                    costPayoutModifier={detailSideSheetCpm}
                    initialTabIndex={detailSideSheetTabIndex}
                    onDismiss={onDismissDetailSideSheet}
                />
            </Pane>
        </RampartAuthorized>
    );
};
